import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  Paper,
  Alert,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import { routes } from '../config';
import { useStoreSettings } from '../contexts/StoreSettingsContext';
import { useLocalization } from '@/contexts/LocalizationContext';

interface ReferralCodeProps {
  onBack: () => void;
}

export default function ReferralCode({ onBack }: ReferralCodeProps) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const navigate = useNavigate();
  const { storeSettings } = useStoreSettings();
  const [referralCode, setReferralCode] = useState('');
  const [error, setError] = useState('');
  const { t } = useLocalization();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!referralCode.trim()) {
      setError(t('referralCode.errorRequired'));
      return;
    }
    
    setError('');
    // Navigate back to register with the referral code
    navigate(routes.register, { 
      state: { referralCode: referralCode.trim() },
      replace: true 
    });
  };

  const formContent = (
    <Box component="form" onSubmit={handleSubmit}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
        }}
      >
        <IconButton onClick={onBack} sx={{ color: '#666' }}>
          <ArrowBack />
        </IconButton>
        
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#333' }}>
          {t('referralCode.title')}
        </Typography>
        
        <Box sx={{ width: 40 }} /> {/* Spacer for centering */}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Illustration */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        mb: 3,
        height: '140px',
        borderRadius: 2,
        alignItems: 'center'
      }}>
        <img 
          src="/images/img_referral_code.svg" 
          alt="Referral Code Illustration" 
          style={{ 
            height: '180px', 
            width: 'auto',
            maxWidth: '100%'
          }} 
        />
      </Box>

      {/* Instruction */}
      <Typography variant="body1" sx={{ textAlign: 'center', mb: 3, color: '#666' }}>
        {t('referralCode.instruction')}
      </Typography>

      {/* Input Field */}
      <TextField
        fullWidth
        placeholder={t('referralCode.placeholder')}
        value={referralCode}
        onChange={(e) => setReferralCode(e.target.value)}
        sx={{
          mb: 3,
          '& .MuiOutlinedInput-root': {
            borderRadius: '99px',
            backgroundColor: '#F6F6F6',
            border: '1px solid #D9E4CE',
            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
            '& .MuiInputBase-input': {
              padding: '10px 16px',
              height: '42px',
              boxSizing: 'border-box',
            },
          },
          '& .MuiInputBase-input::placeholder': {
            color: '#999',
            opacity: 1,
          },
        }}
      />

      {/* Submit Button */}
      <Button
        type="submit"
        fullWidth
        variant="contained"
        sx={{
          height: '44px',
          borderRadius: '99px',
          padding: '10px 16px',
          textTransform: 'none',
          backgroundColor: '#A9C191',
          color: 'white',
          '&:hover': {
            backgroundColor: '#94AF79',
          },
          fontSize: '16px',
          fontWeight: 600,
        }}
      >
        {t('referralCode.submit')}
      </Button>
    </Box>
  );

  // Mobile layout (full screen)
  if (!isDesktop) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          position: 'relative',
          background: `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url("${storeSettings?.backgroundUrl || '/images/background.png'}")`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
      >
        {/* Upper Half - Background with Branding */}
        <Box
          sx={{
            flex: 0.5,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            color: 'white',
            pt: 4,
            pb: 4,
          }}
        >
          <Box sx={{ mb: 2 }}>
            <img src={storeSettings?.logoUrl || '/icons/logo.svg'} alt="Store Logo" style={{ height: '50px', width: 'auto' }} />
          </Box>
          <Typography
            variant="h5"
            component="h2"
            sx={{
              mb: 1,
              textShadow: '0 2px 4px rgba(0,0,0,0.5)',
              fontFamily: 'Montserrat',
              fontWeight: 600,
              fontStyle: 'normal',
              fontSize: '20px',
              color: '#FFFFFF',
              lineHeight: '22px',
              letterSpacing: '0.4px',
              textAlign: 'center',
              verticalAlign: 'middle',
            }}
          >
             {storeSettings?.text || ' Welcome to Grindie Membership Programme'}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              opacity: 0.9,
              textShadow: '0 1px 2px rgba(0,0,0,0.5)',
              color: 'white',
              cursor: 'pointer',
              '&:hover': {
                opacity: 1,
              },
              fontFamily: 'Montserrat',
              fontWeight: 500,
              fontStyle: 'normal',
              fontSize: '14px',
              lineHeight: '22px',
              letterSpacing: '0.28px',
              textAlign: 'center',
              verticalAlign: 'middle',
            }}
            onClick={onBack}
          >
            Sign up to get started
          </Typography>
        </Box>

        {/* Bottom Sheet - White Form Card */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: '24px 24px 0 0',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            p: 4,
            pb: 6,
            width: '100%',
            boxShadow: '0 -8px 32px rgba(0,0,0,0.1)',
          }}
        >
          {formContent}
        </Paper>
      </Box>
    );
  }

  // Desktop layout (centered dialog)
  return (
    <Box
      sx={{
        minHeight: '100vh',
        position: 'relative',
        background: `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url("${storeSettings?.backgroundUrl || '/images/background.png'}")`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
    >
      {/* Upper Half - Background with Branding */}
      <Box
        sx={{
          flex: 0.5,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
          color: 'white',
          pt: 4,
          pb: 4,
        }}
      >
        <Box sx={{ mb: 2 }}>
          <img src={storeSettings?.logoUrl || '/icons/logo.svg'} alt="Store Logo" style={{ height: '50px', width: 'auto', filter: 'brightness(0) invert(1)' }} />
        </Box>
        <Typography
          variant="h5"
          component="h2"
          sx={{
            mb: 1,
            textShadow: '0 2px 4px rgba(0,0,0,0.5)',
            fontFamily: 'Montserrat',
            fontWeight: 600,
            fontStyle: 'normal',
            fontSize: '20px',
            color: '#FFFFFF',
            lineHeight: '22px',
            letterSpacing: '0.4px',
            textAlign: 'center',
            verticalAlign: 'middle',
          }}
        >
           {storeSettings?.text || ' Welcome to Grindie Membership Programme'}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            opacity: 0.9,
            textShadow: '0 1px 2px rgba(0,0,0,0.5)',
            color: 'white',
            cursor: 'pointer',
            '&:hover': {
              opacity: 1,
            },
            fontFamily: 'Montserrat',
            fontWeight: 500,
            fontStyle: 'normal',
            fontSize: '14px',
            lineHeight: '22px',
            letterSpacing: '0.28px',
            textAlign: 'center',
            verticalAlign: 'middle',
          }}
          onClick={onBack}
        >
          Sign up to get started
        </Typography>
      </Box>

      {/* Centered Dialog (instead of bottom sheet) */}
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', pb: 14 }}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: '24px',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
            width: '440px',
            maxWidth: '90vw',
            margin: 'auto',
            p: 4,
            pb: 6,
          }}
        >
          {formContent}
        </Paper>
      </Box>
    </Box>
  );
}
